// 디버그 기능 관리 클래스
class Debug {
  constructor(game) {
    this.game = game;
    this.isEnabled = GameConfig.DEBUG.ENABLED;
    this.setupDebugCommands();
  }

  // 디버그 명령어 설정
  setupDebugCommands() {
    // 한국어 주석: 디버그 기능이 비활성화되어 있으면 실행하지 않음
    if (!this.isEnabled) {
      Utils.log("디버그 모드가 비활성화되어 있습니다.");
      return;
    }

    // 디버그 단축키 설정
    this.setupDebugHotkeys();

    window.gameDebug = {
      // 카메라 모드 변경
      setCameraMode: (mode) => {
        if (this.game && this.game.cameraController) {
          this.game.cameraController.setMode(mode);
          Utils.log(`카메라 모드가 ${mode}로 변경되었습니다.`);
        }
      },

      // 환경 프리셋 변경
      setEnvironment: (preset) => {
        if (this.game && this.game.sceneManager) {
          this.game.sceneManager.applyPreset(preset);
          Utils.log(`환경이 ${preset}로 변경되었습니다.`);
        }
      },

      // 플레이어 이동 속도 변경
      setPlayerSpeed: (speed) => {
        if (this.game && this.game.player) {
          this.game.player.moveSpeed = speed;
          Utils.log(`플레이어 이동 속도가 ${speed}로 변경되었습니다.`);
        }
      },

      // 키 상태 확인
      getKeyStates: () => {
        if (this.game && this.game.inputHandler) {
          return this.game.inputHandler.keys;
        }
        return null;
      },

      // 게임 정보 출력
      getGameInfo: () => {
        if (this.game) {
          return {
            playerPosition: this.game.player
              ? this.game.player.getPosition()
              : null,
            cameraPosition: this.game.camera ? this.game.camera.position : null,
            isPlayerLoaded: this.game.player
              ? this.game.player.isModelLoaded()
              : false,
            gameLoopStatus: this.game.gameLoop
              ? this.game.gameLoop.getStatus()
              : null,
            enemyCount: this.game.enemySpawnManager
              ? this.game.enemySpawnManager.enemies.length
              : 0,
            itemCount: this.game.itemManager
              ? this.game.itemManager.items.length
              : 0,
            playerPoints: this.game.player ? this.game.player.points : 0,
          };
        }
        return null;
      },

      // 새 적 추가 (편의 함수)
      addEnemy: async (enemyType = "MINION", x = 0, z = 0) => {
        if (this.game && this.game.enemySpawnManager) {
          const enemy = await this.game.enemySpawnManager.createEnemy(
            enemyType,
            x,
            z
          );
          return enemy ? `${enemyType} 적 추가 성공` : "적 추가 실패";
        }
        return "게임이 초기화되지 않음";
      },

      // 모든 적 제거
      removeAllEnemies: () => {
        if (this.game) {
          this.game.removeAllEnemies();
          return "모든 적 제거됨";
        }
        return "게임이 초기화되지 않음";
      },

      // 동료 소환 (편의 함수)
      spawnCompanion: async (companionType = "ROGUE") => {
        if (this.game && this.game.companionManager) {
          const result = await this.game.spawnCompanion(companionType);
          return result
            ? `${companionType} 동료 소환 성공`
            : "동료 소환 실패 (최대치 도달)";
        }
        return "게임이 초기화되지 않음";
      },

      // 모든 동료 제거
      removeAllCompanions: () => {
        if (this.game) {
          this.game.removeAllCompanions();
          return "모든 동료 제거됨";
        }
        return "게임이 초기화되지 않음";
      },

      // 모든 엔티티 제거 (적 + 동료)
      clearAll: () => {
        if (this.game) {
          const enemyCount = this.game.enemySpawnManager
            ? this.game.enemySpawnManager.enemies.length
            : 0;
          const companionCount = this.game.companionManager
            ? this.game.companionManager.companions.length
            : 0;

          this.game.removeAllEnemies();
          this.game.removeAllCompanions();

          return `모든 엔티티 제거 완료 (적: ${enemyCount}, 동료: ${companionCount})`;
        }
        return "게임이 초기화되지 않음";
      },

      // 게임 루프 제어
      pauseGame: () => {
        if (this.game && this.game.gameLoop) {
          this.game.gameLoop.stop();
          return "게임 일시정지됨";
        }
        return "게임 루프를 찾을 수 없음";
      },

      resumeGame: () => {
        if (this.game && this.game.gameLoop) {
          this.game.gameLoop.start();
          return "게임 재시작됨";
        }
        return "게임 루프를 찾을 수 없음";
      },

      // 설정 변경
      updateConfig: (path, value) => {
        try {
          const keys = path.split(".");
          let current = GameConfig;

          for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]]) {
              current[keys[i]] = {};
            }
            current = current[keys[i]];
          }

          current[keys[keys.length - 1]] = value;
          Utils.log(`설정 업데이트: ${path} = ${value}`);
          return "설정 업데이트 성공";
        } catch (error) {
          Utils.log("설정 업데이트 실패: " + error.message, "error");
          return "설정 업데이트 실패";
        }
      },

      // 성능 정보 출력
      getPerformanceInfo: () => {
        if (!this.game.renderer) return null;

        return {
          drawCalls: this.game.renderer.info.render.calls,
          triangles: this.game.renderer.info.render.triangles,
          points: this.game.renderer.info.render.points,
          lines: this.game.renderer.info.render.lines,
          frame: this.game.renderer.info.render.frame,
          memory: {
            geometries: this.game.renderer.info.memory.geometries,
            textures: this.game.renderer.info.memory.textures,
          },
        };
      },

      // 플레이어 피격 테스트
      testPlayerHit: (damage = 20) => {
        if (this.game && this.game.player) {
          const result = this.game.player.takeDamage(damage);
          return result
            ? `플레이어가 ${damage} 데미지를 받았습니다!`
            : "플레이어가 피격을 받을 수 없는 상태입니다.";
        }
        return "플레이어를 찾을 수 없습니다.";
      },

      // 적 피격 테스트
      testEnemyHit: (damage = 30) => {
        if (
          this.game &&
          this.game.enemySpawnManager &&
          this.game.enemySpawnManager.enemies.length > 0
        ) {
          const enemy = this.game.enemySpawnManager.enemies[0];
          const result = enemy.takeDamage(damage);
          return result
            ? `적이 ${damage} 데미지를 받아 사망했습니다!`
            : `적이 ${damage} 데미지를 받았습니다!`;
        }
        return "적을 찾을 수 없습니다.";
      },

      // 체력 정보 확인
      getHealthInfo: () => {
        const info = {};
        if (this.game && this.game.player) {
          info.player = this.game.player.getHealthInfo();
        }
        if (this.game && this.game.enemySpawnManager) {
          info.enemies = this.game.enemySpawnManager.enemies.map(
            (enemy, index) => ({
              index,
              ...enemy.getHealthInfo(),
            })
          );
        }
        return info;
      },

      // 플레이어 체력 회복
      healPlayer: (amount = 50) => {
        if (this.game && this.game.player) {
          this.game.player.heal(amount);
          return `플레이어가 ${amount}만큼 회복했습니다!`;
        }
        return "플레이어를 찾을 수 없습니다.";
      },

      // 플레이어 강제 사망 테스트
      killPlayer: () => {
        if (this.game && this.game.player) {
          if (this.game.player.isAlive) {
            this.game.player.health = 0;
            this.game.player.handleDeath();
            // 체력바도 업데이트
            if (this.game.player.healthBar) {
              this.game.player.healthBar.updateHealth(
                0,
                this.game.player.maxHealth
              );
            }
            return `플레이어가 강제로 사망했습니다! 죽음 애니메이션을 확인하세요. (지속시간: ${
              this.game.config?.PLAYER?.DEATH_ANIMATION_DURATION ||
              PlayerConfig.DEATH_ANIMATION_DURATION
            }초)`;
          } else {
            return "플레이어는 이미 죽었습니다.";
          }
        }
        return "플레이어를 찾을 수 없습니다.";
      },

      // 적 강제 사망 테스트
      killEnemy: (index = 0) => {
        if (
          this.game &&
          this.game.enemySpawnManager &&
          this.game.enemySpawnManager.enemies.length > index
        ) {
          const enemy = this.game.enemySpawnManager.enemies[index];
          if (enemy.isAliveCheck()) {
            enemy.die();
            return `${index}번 적이 강제로 사망했습니다! 죽음 애니메이션을 확인하세요.`;
          } else {
            return `${index}번 적은 이미 죽었거나 죽어가는 중입니다.`;
          }
        }
        return `${index}번 적을 찾을 수 없습니다.`;
      },

      // 모든 적 강제 사망
      killAllEnemies: () => {
        if (this.game && this.game.enemySpawnManager) {
          let killedCount = 0;
          this.game.enemySpawnManager.enemies.forEach((enemy, index) => {
            if (enemy.isAliveCheck()) {
              enemy.die();
              killedCount++;
            }
          });
          return `${killedCount}마리의 적이 모두 사망했습니다!`;
        }
        return "적을 찾을 수 없습니다.";
      },

      // 아이템 강제 생성 (통합된 시스템)
      spawnItem: (itemType = "diamond", x = 0, z = 0) => {
        if (this.game && this.game.itemManager) {
          try {
            this.game.itemManager.forceSpawnItem(itemType, x, z);
            return `${itemType} 아이템이 (${x}, ${z}) 위치에 생성되었습니다!`;
          } catch (error) {
            return `${itemType} 아이템 생성 실패: ${error.message}`;
          }
        }
        return "게임이 초기화되지 않았습니다.";
      },

      // 아이템 정보 확인 (통합된 시스템)
      getItemInfo: () => {
        if (this.game && this.game.itemManager) {
          return this.game.itemManager.getStatus();
        }
        return "아이템 매니저를 찾을 수 없습니다.";
      },

      // 모든 아이템 제거 (통합된 시스템)
      removeAllItems: () => {
        if (this.game && this.game.itemManager) {
          this.game.itemManager.clearAllItems();
          return "모든 아이템이 제거되었습니다.";
        }
        return "아이템 매니저를 찾을 수 없습니다.";
      },

      // 플레이어 포인트 추가
      addPoints: (points = 100) => {
        if (this.game && this.game.player) {
          this.game.player.addPoints(points);
          // UI도 즉시 업데이트
          if (this.game.uiManager) {
            this.game.uiManager.updatePoints(this.game.player.points);
          }
          return `${points} 포인트가 추가되었습니다! (총 포인트: ${this.game.player.points})`;
        }
        return "플레이어를 찾을 수 없습니다.";
      },

      // === 게임 종료 시스템 테스트 ===

      // 게임 오버 강제 실행 (테스트용)
      triggerGameOver: () => {
        if (this.game && this.game.handleGameOver) {
          this.game.handleGameOver();
          return "게임 오버가 강제로 실행되었습니다! 적들의 victory 모션과 게임 종료 화면을 확인하세요.";
        }
        return "게임 오버 기능을 찾을 수 없습니다.";
      },

      // 게임 재시작 테스트
      testRestartGame: () => {
        if (this.game && this.game.restartGame) {
          this.game.restartGame();
          return "게임이 재시작되었습니다! 플레이어 상태와 모든 시스템이 리셋되었습니다.";
        }
        return "게임 재시작 기능을 찾을 수 없습니다.";
      },

      // 게임 종료 상태 확인
      isGameOver: () => {
        if (this.game && this.game.isGameOverState) {
          const isOver = this.game.isGameOverState();
          return `게임 종료 상태: ${isOver ? "종료됨" : "진행 중"}`;
        }
        return "게임 종료 상태 확인 기능을 찾을 수 없습니다.";
      },

      // 게임 종료 화면 강제 표시 (테스트용)
      showGameOverScreen: () => {
        if (this.game && this.game.uiManager) {
          // 테스트용 플레이어 정보 생성
          const testPlayerInfo = {
            survivalTime: 125, // 2분 5초
            killCount: 42,
            level: 8,
            points: 15420,
          };

          this.game.uiManager.showGameOverScreen(testPlayerInfo);
          return "게임 종료 화면이 테스트 모드로 표시되었습니다!";
        }
        return "UI 매니저를 찾을 수 없습니다.";
      },

      // 게임 종료 화면 숨기기
      hideGameOverScreen: () => {
        if (this.game && this.game.uiManager) {
          this.game.uiManager.hideGameOverScreen();
          return "게임 종료 화면이 숨겨졌습니다.";
        }
        return "UI 매니저를 찾을 수 없습니다.";
      },

      // 적들의 victory 애니메이션 테스트
      testEnemyVictory: () => {
        if (this.game && this.game.playEnemyVictoryAnimations) {
          this.game.playEnemyVictoryAnimations();
          return "살아있는 모든 적들이 victory 애니메이션을 재생합니다!";
        }
        return "victory 애니메이션 기능을 찾을 수 없습니다.";
      },

      // 파티 스탯 정보 출력
      getPartyStats: () => {
        const stats = {};

        // 파티 버프 계수
        stats.partyBuffCoefficients = { ...Party.partyBuffCoefficients };

        // 플레이어 기본 스탯
        if (this.game && this.game.player) {
          stats.player = {
            baseStats: { ...this.game.player.baseStats },
            currentStats: {
              attackDamage: this.game.player.attackDamage,
              attackRange: this.game.player.attackRange,
              attackCooltime: this.game.player.attackCooltime,
              maxHealth: this.game.player.maxHealth,
              moveSpeed: this.game.player.moveSpeed,
              health: this.game.player.health,
            },
          };
        }

        // 동료들 스탯
        if (this.game && this.game.companionManager) {
          stats.companions = this.game.companionManager.companions.map(
            (companion, index) => ({
              index,
              type: companion.constructor.name,
              baseStats: { ...companion.baseStats },
              currentStats: {
                attackDamage: companion.attackDamage,
                attackRange: companion.attackRange,
                attackCooltime: companion.attackCooltime,
                maxHealth: companion.maxHealth,
                moveSpeed: companion.moveSpeed,
                health: companion.health,
              },
            })
          );
        }

        console.table(stats.partyBuffCoefficients);
        if (stats.player) {
          console.log("플레이어 기본 스탯:", stats.player.baseStats);
          console.log("플레이어 현재 스탯:", stats.player.currentStats);
        }
        if (stats.companions) {
          stats.companions.forEach((comp) => {
            console.log(
              `동료 #${comp.index} (${comp.type}) 기본 스탯:`,
              comp.baseStats
            );
            console.log(
              `동료 #${comp.index} (${comp.type}) 현재 스탯:`,
              comp.currentStats
            );
          });
        }

        return stats;
      },
    };

    this.showDebugHelp();
  }

  // 디버그 단축키 설정
  setupDebugHotkeys() {
    // 한국어 주석: 디버그 기능이 비활성화되어 있으면 핫키 등록하지 않음
    if (!this.isEnabled) return;

    if (!this.game || !this.game.inputHandler) {
      // InputHandler가 아직 초기화되지 않은 경우 나중에 다시 시도
      setTimeout(() => this.setupDebugHotkeys(), 100);
      return;
    }

    const inputHandler = this.game.inputHandler;

    // R - RogueCompanion 스폰
    inputHandler.onKey("keydown", "KeyR", async () => {
      if (this.game.companionManager) {
        const result = await this.game.spawnCompanion("ROGUE");
        Utils.log(
          result
            ? "RogueCompanion이 소환되었습니다!"
            : "RogueCompanion 소환 실패"
        );
      }
    });

    // T - MageCompanion 스폰
    inputHandler.onKey("keydown", "KeyT", async () => {
      if (this.game.companionManager) {
        const result = await this.game.spawnCompanion("MAGE");
        Utils.log(
          result ? "MageCompanion이 소환되었습니다!" : "MageCompanion 소환 실패"
        );
      }
    });

    // Y - BarbarianCompanion 스폰
    inputHandler.onKey("keydown", "KeyY", async () => {
      if (this.game.companionManager) {
        const result = await this.game.spawnCompanion("BARBARIAN");
        Utils.log(
          result
            ? "BarbarianCompanion이 소환되었습니다!"
            : "BarbarianCompanion 소환 실패"
        );
      }
    });

    // F - MinionEnemy 스폰
    inputHandler.onKey("keydown", "KeyF", async () => {
      if (this.game.enemySpawnManager && this.game.player) {
        const playerPos = this.game.player.getPosition();
        const spawnPos =
          this.game.enemySpawnManager.findSafeSpawnPosition(playerPos);
        const enemy = await this.game.enemySpawnManager.createEnemy(
          "MINION",
          spawnPos.x,
          spawnPos.z
        );
        Utils.log(
          enemy ? "MinionEnemy가 스폰되었습니다!" : "MinionEnemy 스폰 실패"
        );
      }
    });

    // G - RogueEnemy 스폰
    inputHandler.onKey("keydown", "KeyG", async () => {
      if (this.game.enemySpawnManager && this.game.player) {
        const playerPos = this.game.player.getPosition();
        const spawnPos =
          this.game.enemySpawnManager.findSafeSpawnPosition(playerPos);
        const enemy = await this.game.enemySpawnManager.createEnemy(
          "ROGUE",
          spawnPos.x,
          spawnPos.z
        );
        Utils.log(
          enemy ? "RogueEnemy가 스폰되었습니다!" : "RogueEnemy 스폰 실패"
        );
      }
    });

    // H - WarriorEnemy 스폰
    inputHandler.onKey("keydown", "KeyH", async () => {
      if (this.game.enemySpawnManager && this.game.player) {
        const playerPos = this.game.player.getPosition();
        const spawnPos =
          this.game.enemySpawnManager.findSafeSpawnPosition(playerPos);
        const enemy = await this.game.enemySpawnManager.createEnemy(
          "WARRIOR",
          spawnPos.x,
          spawnPos.z
        );
        Utils.log(
          enemy ? "WarriorEnemy가 스폰되었습니다!" : "WarriorEnemy 스폰 실패"
        );
      }
    });

    // C - MageEnemy 스폰
    inputHandler.onKey("keydown", "KeyC", async () => {
      if (this.game.enemySpawnManager && this.game.player) {
        const playerPos = this.game.player.getPosition();
        const spawnPos =
          this.game.enemySpawnManager.findSafeSpawnPosition(playerPos);
        const enemy = await this.game.enemySpawnManager.createEnemy(
          "MAGE",
          spawnPos.x,
          spawnPos.z
        );
        Utils.log(
          enemy ? "MageEnemy가 스폰되었습니다!" : "MageEnemy 스폰 실패"
        );
      }
    });

    // V - 모든 엔티티 제거
    inputHandler.onKey("keydown", "KeyV", () => {
      let removedCount = 0;

      // 모든 적 제거
      if (this.game.enemySpawnManager) {
        const enemyCount = this.game.enemySpawnManager.enemies.length;
        this.game.removeAllEnemies();
        removedCount += enemyCount;
      }

      // 모든 동료 제거
      if (this.game.companionManager) {
        const companionCount = this.game.companionManager.companions.length;
        this.game.removeAllCompanions();
        removedCount += companionCount;
      }

      Utils.log(`모든 엔티티가 제거되었습니다! (총 ${removedCount}개)`);
    });

    // E - 업그레이드 창 표시
    inputHandler.onKey("keydown", "KeyE", () => {
      if (this.game.upgradeManager) {
        this.game.upgradeManager.showUpgradeOptions();
        Utils.log("업그레이드 창이 표시되었습니다!");
      }
    });

    Utils.log("디버그 단축키가 설정되었습니다!");
  }

  // 디버그 도움말 출력
  showDebugHelp() {
    if (!this.isEnabled) return;

    Utils.log(`
⌨️ 디버그 단축키:
- R - RogueCompanion 소환
- T - MageCompanion 소환
- Y - BarbarianCompanion 소환
- F - MinionEnemy 스폰
- G - RogueEnemy 스폰
- H - WarriorEnemy 스폰
- C - MageEnemy 스폰
- V - 모든 엔티티 제거 (적 + 동료)
- E - 업그레이드 창 표시

🎮 게임 디버그 명령어:
- gameDebug.setCameraMode('close'|'medium'|'far'|'top') - 카메라 모드 변경
- gameDebug.setEnvironment('day'|'night'|'sunset') - 환경 변경
- gameDebug.setPlayerSpeed(0.1) - 플레이어 속도 변경
- gameDebug.getKeyStates() - 현재 키 상태 확인
- gameDebug.getGameInfo() - 게임 정보 확인
- gameDebug.addEnemy(enemyType, x, z) - 새 적 추가 (MINION, MAGE, ROGUE, WARRIOR)
- gameDebug.removeAllEnemies() - 모든 적 제거
- gameDebug.spawnCompanion(type) - 동료 소환 (ROGUE, MAGE, BARBARIAN)
- gameDebug.removeAllCompanions() - 모든 동료 제거
- gameDebug.clearAll() - 모든 엔티티 제거 (적 + 동료)
- gameDebug.pauseGame() - 게임 일시정지
- gameDebug.resumeGame() - 게임 재시작
- gameDebug.updateConfig('path.to.setting', value) - 설정 변경
- gameDebug.getPerformanceInfo() - 성능 정보 확인

🎯 피격 시스템 테스트:
- gameDebug.testPlayerHit(damage) - 플레이어 피격 테스트
- gameDebug.testEnemyHit(damage) - 적 피격 테스트
- gameDebug.getHealthInfo() - 모든 캐릭터 체력 정보
- gameDebug.healPlayer(amount) - 플레이어 체력 회복
- gameDebug.killPlayer() - 플레이어 강제 사망 테스트

💀 사망 시스템 테스트:
- gameDebug.killEnemy(index) - 특정 적 강제 사망
- gameDebug.killAllEnemies() - 모든 적 강제 사망

🎁 아이템 시스템 테스트 (통합 관리):
- gameDebug.spawnItem(type, x, z) - 아이템 생성 (diamond, heart, star, bomb)
- gameDebug.addPoints(amount) - 플레이어 포인트 추가
- gameDebug.getItemInfo() - 모든 아이템 정보 확인
- gameDebug.removeAllItems() - 모든 아이템 제거

💀 게임 종료 시스템 테스트:
- gameDebug.triggerGameOver() - 게임 오버 강제 실행 (victory 모션 + 종료 화면)
- gameDebug.testRestartGame() - 게임 재시작 테스트
- gameDebug.isGameOver() - 게임 종료 상태 확인
- gameDebug.showGameOverScreen() - 게임 종료 화면 강제 표시 (테스트 데이터)
- gameDebug.hideGameOverScreen() - 게임 종료 화면 숨기기
- gameDebug.testEnemyVictory() - 적들의 victory 애니메이션 테스트

📊 파티 스탯 확인:
- gameDebug.getPartyStats() - 파티 전체 스탯 정보 출력 (버프 계수 + 플레이어 + 동료)
    `);
  }

  // 디버그 기능 활성화/비활성화
  setEnabled(enabled) {
    this.isEnabled = enabled;
    GameConfig.DEBUG.ENABLED = enabled;

    if (enabled) {
      this.setupDebugCommands();
    } else {
      delete window.gameDebug;
    }

    Utils.log(`디버그 모드 ${enabled ? "활성화" : "비활성화"}`);
  }

  // 실시간 성능 모니터링 시작
  startPerformanceMonitor(interval = 1000) {
    if (!this.isEnabled) return;

    if (this.performanceInterval) {
      clearInterval(this.performanceInterval);
    }

    this.performanceInterval = setInterval(() => {
      const info = window.gameDebug.getPerformanceInfo();
      if (info) {
        Utils.log(
          `성능: Draw Calls: ${info.drawCalls}, Triangles: ${info.triangles}`
        );
      }
    }, interval);

    Utils.log("성능 모니터링 시작");
  }

  // 성능 모니터링 중지
  stopPerformanceMonitor() {
    if (this.performanceInterval) {
      clearInterval(this.performanceInterval);
      this.performanceInterval = null;
      Utils.log("성능 모니터링 중지");
    }
  }
}

// webpack 빌드를 위해 default export 추가
export default Debug;
